import {
  BoxOfferAutonomousPurchase,
  BoxOfferInvitationStatus,
  BoxOfferRuleSchema,
  BoxOfferType,
} from "@modules/boxOffer/entities/boxOfferTypes"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PriceObjectDto, PriceObjectDtoSchema } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import { UserDtoSchema } from "@modules/profile/profileDtos"
import { ProgramType } from "@modules/programs/programTypes"
import { z } from "zod"

export enum StatusFilter {
  ALL = "ALL",
  ACTIVE = "ACTIVE",
  ARCHIVED = "ARCHIVED",
}

export const BoxOfferInvitationCouponSchema = z.object({
  type: z.nativeEnum(CouponType),
  value: z.number(),
  durationInMonths: z.number().nullable(),
})

export const BoxOfferInvitationInputSchema = z.object({
  offerId: z.string(),
  userId: z.string(),
  coupon: BoxOfferInvitationCouponSchema.optional(),
  deferredDate: z.string().optional(),
})

export const BoxOfferInvitationDtoSchema = z.object({
  id: z.string(),
  boxOfferId: z.string(),
  boxId: z.string(),
  invitee: UserDtoSchema,
  inviter: UserDtoSchema,
  status: z.nativeEnum(BoxOfferInvitationStatus),
  createdAt: z.string(),
  coupon: BoxOfferInvitationCouponSchema.nullable(),
  deferredDate: z.string().nullable(),
  updatedAtDate: z.string().nullable(),
})

export const BoxOfferDraftDtoSchema = z.object({
  type: z.nativeEnum(BoxOfferType),
  name: z.string(),
  description: z.string(),
  maxWeeklyParticipation: z.number().nullable(),
  showcaseDisplay: z.boolean(),
  priceDisplay: z.boolean(),
  autonomousPurchase: z.nativeEnum(BoxOfferAutonomousPurchase),
  minimumCommitmentPeriod: z.string().optional(),
  count: z.number().optional(),
  programs: z.array(z.string()),
  rules: z.array(BoxOfferRuleSchema).optional(),
  validityPeriodMonths: z.number().optional(),
  renewable: z.boolean().optional(),
  priceObject: PriceObjectDtoSchema.optional(),
  archived: z.boolean(),
  isFree: z.boolean().optional(),
  currency: z.string().optional(),
})

export type BoxOfferDraftDto = z.infer<typeof BoxOfferDraftDtoSchema>

export const BoxOfferItemDtoSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(BoxOfferType),
  name: z.string(),
  description: z.string().optional(),
  priceObject: PriceObjectDtoSchema.nullable(), // If null the offer is free
  minimumCommitmentPeriod: z.string().nullable(), // If null the offer don't have a minimum commitment period
  count: z.number().nullable(),
  validityPeriodMonths: z.number().nullable(),
  renewable: z.boolean().nullable(),
  showcaseDisplay: z.boolean(), // Show the box offer to users when exploring, default value true
  priceDisplay: z.boolean(), // Show the box offer price to users when exploring, default value true
  autonomousPurchase: z.nativeEnum(BoxOfferAutonomousPurchase), // Set who can buy the offer, default value is PUBLIC(anyone with an hustleup account can buy)
  archived: z.boolean(),
  /**
   * Deprecated
   */
  price: z.number(),
  currency: z.string().nullable(),
  isFree: z.boolean(),
})

export type BoxOfferItemDto = z.infer<typeof BoxOfferItemDtoSchema>

export const BoxOfferDetailedDtoSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(BoxOfferType),
  name: z.string(),
  description: z.string().nullable(),
  priceObject: PriceObjectDtoSchema.nullable(), // If null the offer is free
  minimumCommitmentPeriod: z.string().nullable(), // If null the offer don't have a minimum commitment period
  count: z.number().nullable(),
  maxWeeklyParticipation: z.number().nullable(),
  validityPeriodMonths: z.number().nullable(), // If null the offer don't have a validity period, it's unlimited
  renewable: z.boolean().nullable(),
  showcaseDisplay: z.boolean(),
  priceDisplay: z.boolean(),
  autonomousPurchase: z.nativeEnum(BoxOfferAutonomousPurchase),
  archived: z.boolean(),
  programs: z.array(z.string()), // Array of program ids
  rules: z.array(BoxOfferRuleSchema), // If null the offer don't have rules
  /**
   * Deprecated
   */
  currency: z.string().nullable(),
  isFree: z.boolean(),
})

// Use for mobile dto for box offer user view in invitation
export const MobileBoxProgramRefDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: HUImageDtoSchema.nullable(),
  type: z.nativeEnum(ProgramType),
})

export const MobileBoxOfferItemDtoSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(BoxOfferType),
  name: z.string(),
  description: z.string(),
  priceObject: PriceObjectDtoSchema.nullable(), // If null the offer is free
  minimumCommitmentPeriod: z.string().nullable(), // If null the offer don't have a minimum commitment period
  count: z.number().nullable(),
  validityPeriodMonths: z.number().nullable(), // If null the offer don't have a validity period, it's unlimited
  renewable: z.boolean().nullable(),
  showcaseDisplay: z.boolean(),
  priceDisplay: z.boolean(),
  autonomousPurchase: z.nativeEnum(BoxOfferAutonomousPurchase),
  canUserPurchase: z.boolean(),
  rules: z.array(BoxOfferRuleSchema), // If null the offer don't have rules
  currency: z.string().nullable(),
})

export const BoxOfferMobileDetailedDtoSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(BoxOfferType),
  name: z.string(),
  description: z.string().nullable(),
  priceObject: PriceObjectDtoSchema.nullable(), // If null the offer is free
  minimumCommitmentPeriod: z.string().nullable(), // If null the offer don't have a minimum commitment period
  count: z.number().nullable(),
  maxWeeklyParticipation: z.number().nullable(),
  validityPeriodMonths: z.number().nullable(), // If null the offer don't have a validity period, it's unlimited
  renewable: z.boolean().nullable(),
  showcaseDisplay: z.boolean(),
  priceDisplay: z.boolean(),
  autonomousPurchase: z.nativeEnum(BoxOfferAutonomousPurchase),
  canUserPurchase: z.boolean(),
  programs: MobileBoxProgramRefDtoSchema.array(), // Array of program ids
  rules: z.array(BoxOfferRuleSchema), // If null the offer don't have rules
  hasAlreadyPurchase: z.boolean().nullable(),
  currency: z.string().nullable(),
  invitation: BoxOfferInvitationDtoSchema.nullable(),
})

export const BoxSubscriptionOfferSchema = BoxOfferDetailedDtoSchema.extend({
  type: z.literal(BoxOfferType.Subscription),
})

export const DropInOfferSchema = BoxOfferDetailedDtoSchema.extend({
  type: z.literal(BoxOfferType.DropIn),
})

export const TrialOfferSchema = BoxOfferDetailedDtoSchema.extend({
  type: z.literal(BoxOfferType.Trial),
  count: z.number(),
  renewable: z.boolean(),
})

export const TicketsBookOfferSchema = BoxOfferDetailedDtoSchema.extend({
  type: z.literal(BoxOfferType.TicketsBook),
  count: z.number(),
})

export const MilleniumOfferSchema = BoxOfferDetailedDtoSchema.extend({
  type: z.literal(BoxOfferType.Millenium),
  priceObject: z.literal(null),
  isFree: z.literal(true),
})

export type BoxSubscriptionOffer = z.infer<typeof BoxSubscriptionOfferSchema>
export type DropInOffer = z.infer<typeof DropInOfferSchema>
export type TrialOffer = z.infer<typeof TrialOfferSchema>
export type TicketsBookOffer = z.infer<typeof TicketsBookOfferSchema>
export type MilleniumOffer = z.infer<typeof MilleniumOfferSchema>
export type BoxOfferDetailedDto = z.infer<typeof BoxOfferDetailedDtoSchema>
export type BoxOfferInvitationInput = z.infer<typeof BoxOfferInvitationInputSchema>
export type BoxOfferInvitationDto = z.infer<typeof BoxOfferInvitationDtoSchema>
export type MobileBoxOfferItemDto = z.infer<typeof MobileBoxOfferItemDtoSchema>
export type BoxOfferMobileDetailedDto = z.infer<typeof BoxOfferMobileDetailedDtoSchema>
export type MobileBoxProgramRefDto = z.infer<typeof MobileBoxProgramRefDtoSchema>
export type BoxOfferInvitationCoupon = z.infer<typeof BoxOfferInvitationCouponSchema>

export type LightBoxOfferInfoDto = {
  id: string
  name: string
  price?: PriceObjectDto
  type: BoxOfferType
}

export type BoxPurchaseDto = {
  id: string
  date: string
  status: string
  offer: { id: string; name: string; type: BoxOfferType }
  count?: number
  initialCount?: number
  price: PriceObjectDto
  purchaseDate: string
}
